<template>
  <div class="carousel-wrapper">
    <div class="carousel-container">
      <div 
        class="carousel-track" 
        :style="{ transform: `translateX(${offset}%)`, transition: transitionStyle }"
        @mouseenter="stopAutoPlay"
        @mouseleave="startAutoPlay"
        @touchstart="handleTouchStart"
        @touchend="handleTouchEnd"
      >
        <div 
          class="carousel-item" 
          v-for="(item, index) in items" 
          :key="index"
        >
          <div class="item">
            <img :src="item" style="width: 100%;height: 100%;">
          </div>
        </div>
      </div>
    </div>
    
    <button class="carousel-btn prev" @click="prevSlide">‹</button>
    <button class="carousel-btn next" @click="nextSlide">›</button>
  </div>
</template>

<script>
export default {
  name: 'ImageCarousel',
  props: {
    items: {
      type: Array,
      required: true,
      validator: value => value.every(item => item.image && item.alt)
    },
    autoPlay: {
      type: Boolean,
      default: true
    },
    interval: {
      type: Number,
      default: 1500
    }
  },
  data() {
    return {
      currentIndex: 0,
      visibleItems: 6,
      offset: 0,
      transitionStyle: 'transform 0.5s ease',
      autoPlayInterval: null,
      touchStartX: 0,
      touchEndX: 0
    }
  },
  computed: {
    totalItems() {
      return this.items.length
    }
  },
  methods: {
    updateVisibleItems() {
      if (window.innerWidth <= 480) return 1
      if (window.innerWidth <= 768) return 2
      if (window.innerWidth <= 1024) return 3
      return 6
    },
    updateCarousel() {
      this.visibleItems = this.updateVisibleItems()
      const itemWidth = 100 / this.visibleItems
      this.offset = -this.currentIndex * itemWidth
    },
    nextSlide() {
      this.visibleItems = this.updateVisibleItems()
      this.currentIndex++
      
      if (this.currentIndex > this.totalItems - this.visibleItems) {
        // 禁用过渡效果
        this.transitionStyle = 'none'
        this.currentIndex = 0
        this.updateCarousel()
        
        // 强制重绘
        this.$nextTick(() => {
          this.transitionStyle = 'transform 0.5s ease'
        })
      } else {
        this.updateCarousel()
      }
    },
    prevSlide() {
      this.visibleItems = this.updateVisibleItems()
      this.currentIndex--
      
      if (this.currentIndex < 0) {
        // 禁用过渡效果
        this.transitionStyle = 'none'
        this.currentIndex = this.totalItems - this.visibleItems
        this.updateCarousel()
        
        // 强制重绘
        this.$nextTick(() => {
          this.transitionStyle = 'transform 0.5s ease'
        })
      } else {
        this.updateCarousel()
      }
    },
    startAutoPlay() {
      if (this.autoPlay) {
        this.stopAutoPlay()
        this.autoPlayInterval = setInterval(this.nextSlide, this.interval)
      }
    },
    stopAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval)
        this.autoPlayInterval = null
      }
    },
    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX
      this.stopAutoPlay()
    },
    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX
      this.handleSwipe()
      this.startAutoPlay()
    },
    handleSwipe() {
      if (this.touchEndX < this.touchStartX - 50) {
        this.nextSlide()
      } else if (this.touchEndX > this.touchStartX + 50) {
        this.prevSlide()
      }
    },
    handleResize() {
      this.updateCarousel()
    }
  },
  mounted() {
    this.updateCarousel()
    this.startAutoPlay()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    this.stopAutoPlay()
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style scoped>
.carousel-wrapper {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  padding: 0 60px;
  background-color: #595857;
  overflow: hidden; /* 新增：隐藏溢出的内容 */
}

.carousel-container {
  position: relative;
  width: calc(100% - 120px); /* 调整：减去两侧按钮的空间 */
  margin: 0 auto; /* 新增：居中容器 */
  overflow: hidden;
  padding: 20px;
  background-color: #595857;
}

.carousel-track {
  display: flex;
  will-change: transform;
  background-color: #595857;
}

.carousel-item {
  flex: 0 0 calc(100% / 6);
  padding: 0 15px;
  box-sizing: border-box;
  min-width: 0; /* 新增：防止flex项目溢出 */
}

.carousel-item .item {
  width: 100%; /* 修改：使用100%宽度 */
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  background-color: black;
}

.carousel-item .item:hover {
  transform: scale(1.05);
}

.carousel-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  background-color: transparent;
  border: none;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  color: gray;
}

.carousel-btn:hover {
  background-color: #007FFF;
  color: white;
  transform: translateY(-50%) scale(1.1);
}

.prev {
  left: 10px; /* 调整：统一按钮间距 */
}

.next {
  right: 10px; /* 调整：统一按钮间距 */
}

@media (max-width: 1024px) {
  .carousel-item {
    flex: 0 0 calc(100% / 3);
  }
  
  .carousel-container {
    width: calc(100% - 80px); /* 响应式调整宽度 */
  }
}

@media (max-width: 768px) {
  .carousel-item {
    flex: 0 0 calc(100% / 2);
  }
  
  .carousel-wrapper {
    padding: 0 40px;
  }
  
  .carousel-container {
    width: calc(100% - 60px); /* 响应式调整宽度 */
  }
  
  .prev {
    left: 5px;
  }
  
  .next {
    right: 5px;
  }
}

@media (max-width: 480px) {
  .carousel-item {
    flex: 0 0 100%;
  }
  
  .carousel-wrapper {
    padding: 0 30px;
  }
  
  .carousel-container {
    width: calc(100% - 40px); /* 响应式调整宽度 */
  }
}
</style>